/**
 * Focus Management Hook
 * 
 * Provides utilities for managing focus in accessible ways.
 */

import { useEffect, useRef, RefObject } from 'react'

/**
 * Hook to trap focus within a container (for modals, dialogs)
 */
export function useFocusTrap(isActive: boolean): RefObject<HTMLElement> {
  const containerRef = useRef<HTMLElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!isActive || !containerRef.current) return

    // Store the previously focused element
    previousActiveElement.current = document.activeElement as HTMLElement

    const container = containerRef.current

    // Get all focusable elements
    const getFocusableElements = (): HTMLElement[] => {
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(', ')

      return Array.from(container.querySelectorAll<HTMLElement>(selector))
        .filter(el => {
          // Filter out hidden elements
          const style = window.getComputedStyle(el)
          return style.display !== 'none' && style.visibility !== 'hidden'
        })
    }

    const focusableElements = getFocusableElements()
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    // Focus the first element
    if (firstElement) {
      firstElement.focus()
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      if (e.shiftKey) {
        // Shift + Tab (backwards)
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement?.focus()
        }
      } else {
        // Tab (forwards)
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement?.focus()
        }
      }
    }

    container.addEventListener('keydown', handleKeyDown)

    return () => {
      container.removeEventListener('keydown', handleKeyDown)
      // Restore focus to previous element
      previousActiveElement.current?.focus()
    }
  }, [isActive])

  return containerRef
}

/**
 * Hook to restore focus to a specific element when unmounting
 */
export function useRestoreFocus(elementRef: RefObject<HTMLElement>) {
  const previousActiveElement = useRef<HTMLElement | null>(null)

  useEffect(() => {
    // Store current focus
    previousActiveElement.current = document.activeElement as HTMLElement

    return () => {
      // Restore focus on unmount
      if (elementRef.current) {
        elementRef.current.focus()
      } else if (previousActiveElement.current) {
        previousActiveElement.current.focus()
      }
    }
  }, [elementRef])
}

/**
 * Hook to manage focus for skip links
 */
export function useSkipLink(targetId: string) {
  const handleSkip = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const target = document.getElementById(targetId)
    if (target) {
      target.focus()
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return { handleSkip }
}

/**
 * Hook to announce messages to screen readers
 */
export function useAriaLive(message: string | null, priority: 'polite' | 'assertive' = 'polite') {
  useEffect(() => {
    if (!message) return

    const liveRegion = document.createElement('div')
    liveRegion.setAttribute('role', 'status')
    liveRegion.setAttribute('aria-live', priority)
    liveRegion.setAttribute('aria-atomic', 'true')
    liveRegion.className = 'sr-only'
    liveRegion.textContent = message

    document.body.appendChild(liveRegion)

    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(liveRegion)
    }, 1000)

    return () => {
      if (document.body.contains(liveRegion)) {
        document.body.removeChild(liveRegion)
      }
    }
  }, [message, priority])
}

export default useFocusTrap

